import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { QuestionModal, Spinner } from '../../../../common/index.jsx';
import { AppBarCMS } from '../../../../basic/index.jsx';
import { routes } from '../../../../../utils/helpers/ApiClient.jsx';
import { PlusOutlined } from '@ant-design/icons';
import ExternalUsersTable from './ExternalUsersTable.jsx';
import {
  useActivateExternalUserMutation,
  useDeactivateExternalUserMutation,
  useDeleteExternalUserMutation,
  useGetExternalUsersQuery,
} from '../../../../../redux/services/externalUsers.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages';

const ExternalUsersPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = t('Externí používatelia') + ' - FRI CMS';
  }, []); // Runs on mount

  const { deleteSuccess: toastDeleteSuccess, saveSuccess: toastSaveSuccess } =
    useToastMessages();

  const { data: usersData, isLoading, refetch } = useGetExternalUsersQuery();
  const [deleteExternalUser, { isSuccess: deleteSuccess }] =
    useDeleteExternalUserMutation();
  const [activateExternalUser] = useActivateExternalUserMutation();
  const [deactivateExternalUser] = useDeactivateExternalUserMutation();

  const [rowToBeActivated, setRowToBeActivated] = useState(null);
  const [rowToBeDeactivated, setRowToBeDeactivated] = useState(null);

  useEffect(() => {
    if (deleteSuccess) {
      toastDeleteSuccess();
      refetch();
    }
  }, [deleteSuccess]);

  const handleActivate = async (id) => {
    try {
      await activateExternalUser(id).unwrap();
      toastSaveSuccess(
        t('Externému používateľovi bol opätovne povolený prístup.'),
      );
      refetch();
    } catch (err) {}
  };

  const handleDeactivate = async (id) => {
    try {
      await deactivateExternalUser(id).unwrap();
      toastSaveSuccess(t('Externému používateľovi bol zrušený prístup.'));
      refetch();
    } catch (err) {}
  };

  const handleDelete = async (id) => {
    try {
      await deleteExternalUser(id).unwrap();
      // toastDeleteSuccess sa zavolá v useEffect vyššie
    } catch (err) {}
  };

  return isLoading ? (
    <Spinner />
  ) : (
    <>
      <AppBarCMS
        title={t('Externí používatelia')}
        backUrl={routes.cmsDashboard}
        backUrlTooltip={t('Nástenka')}
        mainActions={[
          {
            title: t('Pridať externého používateľa'),
            icon: <PlusOutlined />,
            permissions: 'external-users-admin',
            onClick: () => {
              navigate(routes.cmsExternalUsersCreate);
            },
          },
        ]}
      />
      <div style={{ margin: '10px' }}>
        <ExternalUsersTable
          permissionsBasic={'external-users'}
          usersData={usersData}
          uniqueTableId={'external-users-table'}
          refetch={refetch}
          onEditClick={(record) =>
            navigate(routes.cmsExternalUsersEdit(record.id))
          }
          onDeleteClick={(record) => handleDelete(record.id)}
          onActivateClick={(record) => setRowToBeActivated(record)}
          onDeactivateClick={(record) => setRowToBeDeactivated(record)}
        />
      </div>

      <QuestionModal
        chosenPositive={() => handleActivate(rowToBeActivated.id)}
        question={t(
          'Naozaj chcete znovu povoliť prístup externému používateľovi?',
        )}
        visible={rowToBeActivated !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeActivated(null)}
        headerTitle={t('Povoliť prístup')}
      />

      <QuestionModal
        chosenPositive={() => handleDeactivate(rowToBeDeactivated.id)}
        question={t('Naozaj chcete zrušiť prístup externému používateľovi?')}
        visible={rowToBeDeactivated !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeDeactivated(null)}
        headerTitle={t('Zrušiť prístup')}
      />
    </>
  );
};

export default ExternalUsersPage;
